import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react';
import Select, { components } from 'react-select';
import { DateRange } from 'react-date-range';
import { useStaticQuery, graphql } from "gatsby";
import useBrowserMode from '../../../shared/hooks/useBrowserMode';
import { getToken, getCmdFormData } from "../../../services/formServices";

// Components //
import CategoryTag from "../../atoms/category-tag/categoryTag";
import MediaCard from '../../molecules/media-card/mediaCard';
import Pagination from "../../molecules/pagination/Pagination";
import FilterMobile from '../filter-mobile/filterMobile';
import SearchContent from "../../../components/molecules/search-content/searchContent"
import Loading from "../../atoms/loading/loading";

// Interfaces //
import { ReferenciaContenido } from "../../../interfaces/ReferenciaContenido";
import { ReferenciaMultimedia } from "../../../interfaces/ReferenciaMultimedia";

// Styles //
import "./librarySection.scss";
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file

// Helpers //
import { getStartOfWeek, getStartOfMonth, getStartOfYear, formatDate } from "../../../utils/helpers"

// Data // 
import { Dates, Categories, Themes } from "../../../data/biblioteca.data.js";

// Context //
import GlobalContext from "../../../contexts/globalContext";
import ModalGuiaCdc from '../../molecules/modal-guia-cdc/modalGuiaCdc';

let PageSize = 9;

interface SelectOptionInterface{
    value: string,
    label: string
}

const LibrarySection = (props: ReferenciaContenido) => {
    const data = useStaticQuery(graphql`
        query BIBLIOTECAICONS {
            iconFilter: allContentfulAsset(
                filter: { title: { eq: "icon-filtros" } }
            ) {
                nodes {
                    file {
                        url
                    }
                }
            }
            dataSub: contentfulSubcategorias(contentful_id: {eq: "2SmuRXdJp2FuOoTdiVoL5D"}) {
                referenciaSubcategorias {
                    slug
                    referenciaInterna {
                        referenciaContenido {
                            contentful_id
                        }
                        referenciaCabecera {
                            migaDePan
                            referenciaMultimedia {
                                audioUrl
                                autor
                                categoriaMultimedia
                                contentful_id
                                createdAt
                                descripcion {
                                    raw
                                }
                                imagenPrincipal {
                                    gatsbyImageData(
                                        layout: CONSTRAINED
                                        outputPixelDensities: [0.5, 1, 2]
                                        breakpoints: [320, 768, 1024, 1440]
                                        placeholder: BLURRED
                                        sizes: "(max-width: 320px) 320px, (max-width: 768px) 768px, (max-width: 1024px) 1024px, 1440px"
                                    )
                                    description                                    
                                }
                                nuevoContenido
                                tematica
                                textoIntroductorio
                                tiempoLectura
                                tituloBoton
                                tituloMultimedia
                                videoUrl
                                video {
                                    file {
                                        url
                                        fileName
                                        contentType
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }         
    `);

    const transformData = (arr) => {
        return arr.map((sub) => {
            return {
                ...sub.referenciaInterna.referenciaCabecera.referenciaMultimedia,
                slug: sub.slug,
                parentContentfulId: sub.referenciaInterna.referenciaContenido[0].contentful_id
            }
        })
    }

    const allData = transformData(data.dataSub.referenciaSubcategorias);
    const iconFilter: string = data.iconFilter.nodes[0].file.url;
    const { window } = useBrowserMode();
    const Context = useContext(GlobalContext);

    const [loadedList] = useState(allData.map(obj => { return { ...obj, date: new Date(obj.createdAt) } }).sort((a:any, b: any) => a.date - b.date).reverse())
    const [filteredListTheme, setFilteredListTheme] = useState<ReferenciaMultimedia[]>(loadedList);
    const [filteredList, setFilteredList] = useState<ReferenciaMultimedia[]>(filteredListTheme);
    const [selectedDate, setSelectedDate] = useState<SelectOptionInterface[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<SelectOptionInterface[]>([]);
    const [activedTheme, setActivedTheme] = useState<string>("Todos");
    const [showDateRange, setShowDateRange] = useState<boolean>(false);
    const [date, setDate] = useState([{startDate: new Date(), endDate: null, key: 'selection'}]);
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilter, setShowFilter] = useState<boolean>(false);
    const dateRangeRef = useRef(null);
    const posRef = useRef<HTMLDivElement>(null);
    const { searchWordContent } = useContext(GlobalContext);
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

    const [loginData, setLoginData] = useState({
        fullName: "",
        companyRole: "",
        documentId: "",
        email: "",
        mobile: "",
        isRegistered: false,
        hasAccess: false, 
      });

    const currentFilteredList = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * PageSize;
        const lastPageIndex = firstPageIndex + PageSize;
        return filteredList.slice(firstPageIndex, lastPageIndex);
    }, [currentPage, filteredList]);
    
    const filteredByTheme = (theme: string) => {
        const categoryList = selectedCategory.map(item => item.value);
        setCurrentPage(1)
        if (theme === "Todos") {
            const filteredList = loadedList.filter((item) => item.tematica !== "Todos");
            if (categoryList.length > 0) setFilteredListTheme(filteredList.filter(item => categoryList.includes(item.categoriaMultimedia)));
            else setFilteredListTheme(filteredByKeyWord(searchWordContent, filteredList));
        } else {
            const filteredList = loadedList.filter((item) => item.tematica === theme);
            if (categoryList.length > 0) setFilteredListTheme(filteredList.filter(item => categoryList.includes(item.categoriaMultimedia)));
            else setFilteredListTheme(filteredByKeyWord(searchWordContent, filteredList));
        }
    }  

    const filteredByDate = (option: any) => {
        setSelectedDate(option)
        const sortedDates = filteredListTheme.map(obj => { return { ...obj, date: new Date(obj.createdAt) } }).sort((a:any, b: any) => a.date - b.date);

        if (option.value === "Todos") {
            setFilteredList(loadedList);
        }
        
        if (option.value === "Más recientes") {
            setFilteredList(sortedDates.reverse());
            setShowDateRange(false);
        }

        if (option.value === "Más antiguos") {
            setFilteredList(sortedDates);
            setShowDateRange(false);
        }

        if (option.value === "Esta semana") {
            const startWeekDay = getStartOfWeek(new Date());
            const filteredListWeek = filteredListTheme.map(obj => { return { ...obj, date: new Date(obj.createdAt) } }).filter(obj => obj.date >= startWeekDay);
            setFilteredList(filteredListWeek);
            setShowDateRange(false);
        }
        
        if (option.value === "Este mes") {
            const startMonthDay = getStartOfMonth(new Date());
            const filteredListMonth = filteredListTheme.map(obj => { return { ...obj, date: new Date(obj.createdAt) } }).filter(obj => obj.date >= startMonthDay);
            setFilteredList(filteredListMonth);
            setShowDateRange(false);
        }

        if (option.value === "Este año") {
            const startYearDay = getStartOfYear(new Date());
            const filteredListYear = filteredListTheme.map(obj => { return { ...obj, date: new Date(obj.createdAt) } }).filter(obj => obj.date >= startYearDay);
            setFilteredList(filteredListYear);
            setShowDateRange(false);
        }

        if (option.value === "Fecha específica") {
            setShowDateRange(true);
        }
    }
    
    const filteredByCategory = (category: any) => {
        if (category.length > 0) {
            const categoryList = category.map(item => item.value);
            if (categoryList.includes("Todos") && category[0].value === "Todos") {
                setSelectedCategory(Categories);
            } else if (categoryList.includes("Todos") && category[0].value !== "Todos") {
                setSelectedCategory(Categories);
            }

            if (activedTheme === "Todos"){
                setFilteredList(loadedList.filter(item => categoryList.includes(item.categoriaMultimedia)))
            } else {
                setFilteredList(filteredListTheme.filter(item => categoryList.includes(item.categoriaMultimedia)))
            }
        }

        if(category.length === 0) {
            setFilteredList(filteredListTheme);
        }
    }

    const filteredBySpecificDate = () => {
        if (date[0].startDate) {
            const filteredList = filteredListTheme.map(obj => { return { ...obj, date: new Date(obj.createdAt) } }).filter(obj => obj.date >= date[0].startDate && obj.date <= date[0].endDate);
            setFilteredList(filteredList);

            if (showDateRange && date[0].endDate !== date[0].startDate) {
                setShowDateRange(false);
                setSelectedDate([{
                    value: formatDate(date[0].startDate).replace(/de/gi, "") + " - " + formatDate(date[0].endDate).replace(/de/gi, ""),
                    label: formatDate(date[0].startDate).replace(/de/gi, "") + " - " + formatDate(date[0].endDate).replace(/de/gi, "")
                }])
            }
        }
    }

    const filteredByKeyWord = (keyWord: string, array) => {
        return array.filter(item => item.tituloMultimedia.toLowerCase().includes(keyWord.toLowerCase()));
    }
    
    useEffect(() => {
        filteredBySpecificDate();
    }, [date])
    
    useEffect(() => {
        filteredByCategory(selectedCategory);
    },[selectedCategory])
        
    useEffect(() => {
        filteredByTheme(activedTheme);
    },[activedTheme, searchWordContent])

    useEffect(() => {
        setFilteredList(filteredListTheme);
    },[filteredListTheme])

    const styleSelect = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            borderColor: state.isSelected ? "#ee2b7b": "#cccccc",
            boxShadow: "#ee2b7b",
            ":hover": {
                borderColor: "#ee2b7b",
            }
        }),
        option: (baseStyles, state) => ({
            ...baseStyles,
            color: state.isSelected ? "#ee2b7b" : "",
            backgroundColor: state.isSelected  ? "transparent" : "",
            ":hover": {
                color: "#ee2b7b",
            }
        }),
        multiValue: (styles, { data }) => {
            return {
                ...styles,
                backgroundColor: "#ee2b7b",
            };
        },
        multiValueLabel: (styles, { data }) => ({
            ...styles,
            color: "#ffffff",
        }),
        multiValueRemove: (styles, { data }) => ({
            ...styles,
            color: "#ffffff",
            ':hover': {
                backgroundColor: "#ee2b7b",
                color: 'white',
            },
        }),
    }

    const Option = (props) => {
        return (
            <div>
                <components.Option {...props}>
                    <input
                        type="checkbox"
                        checked={props.isSelected}
                        onChange={() => null}
                    />{" "}
                    <label>{props.label}</label>
                </components.Option>
            </div>
        );
    };

    const getGigyaData = useCallback(async () => {
        const gigyaAccount = Context?.gigyaContext?.gigyaAccount;
        if (gigyaAccount?.profile?.email) {
            try {
                const token = await getToken(gigyaAccount.UID);
                const responseJson = await getCmdFormData(gigyaAccount.data.docNumber, token);

                const registeredFlag = !!responseJson.data.answerData;
                const servicesAuthEnabled = Array.isArray(gigyaAccount.data.servicesAuth) && gigyaAccount.data.servicesAuth.length > 0;
                let hasServiceAccess = false;

                if (servicesAuthEnabled) {
                    for (const service of gigyaAccount.data.servicesAuth) {
                        if (service.serviceId === "COMDADEMPL") {
                            hasServiceAccess = true;
                            break;
                        }
                    }
                }

                setLoginData((prevLoginData) => ({
                    ...prevLoginData,
                    hasAccess: hasServiceAccess,
                    isRegistered: registeredFlag,
                    fullName: `${gigyaAccount.profile.firstName} ${gigyaAccount.profile.lastName}`,
                    documentId: gigyaAccount.data.docNumber,
                    email: gigyaAccount.profile.email,
                    mobile: gigyaAccount.data.mobile,
                }));
            } catch (error) {
                console.error("Error fetching access data", error);
            }
        }
    },[Context.gigyaContext.gigyaAccount]);

    useEffect(() => {
        if (!Context?.gigyaContext?.isLoadingGigya) {
            if (!Context?.gigyaContext?.gigyaAccount) {
                Context?.gigyaContext?.loginComfamaId?.();
            } else {
                getGigyaData();
            }
        }
    },[Context.gigyaContext.isLoadingGigya, Context.gigyaContext.gigyaAccount])

    if(Context.gigyaContext.isLoadingGigya) return <Loading/>;

    const closeModal = () => {
		document.body.style.overflow = "scroll";
		setIsOpenModal((setShow) => !setShow);
	};

    if(!loginData.hasAccess){
        let handleLoginClick = () => {
            const checkGuideCdc = localStorage.getItem("guideCdc");
            if (checkGuideCdc !== "true"){
                setIsOpenModal(true);
            } else {
                Context?.gigyaContext?.loginComfamaId?.();
            }
        };
        return (
            <>
                <div className='o-library-section__showcontent'>
                    <h3> Si quieres ver el contenido de la biblioteca de comunidades debes <a href="#" onClick={handleLoginClick}>iniciar sesion</a> con tu usuario registrado comunidades</h3>
                </div>
                {isOpenModal && (
                    <ModalGuiaCdc showModal={isOpenModal} onClose={closeModal}/>
                )}
            </>
        );
    }

    return (
        <>
            {loginData.hasAccess && (
                <div className="o-library-section" ref={posRef}>
                <div className="container">
                    <div className="o-library-section__container">
                        <div className="o-library-section__container__search">
                            <ul className="breadcrum">
                                <li>
                                    <a href=""><span>Biblioteca </span></a>
                                </li>
                                <li>
                                    <a href=""><span>Resultados de búsqueda </span></a>
                                </li>
                            </ul>
                            <p>Resultado</p>
                            <SearchContent type="bibliotecaComunidadesEmpleo"/>
                        </div>
                        <div className="o-library-section__container__header">
                            <div className="library-filters">
                                <div className="library-filters__theme">
                                    <div className="library-filters__theme__left">
                                        <p>Temática:</p>
                                        {Themes.map(theme => (
                                            <CategoryTag label={theme.value} active={activedTheme} onActive={() => setActivedTheme(theme.value)}/>
                                        ))}
                                    </div>
                                    <div className="library-filters__theme__right">
                                        <button onClick={() => setShowFilter(!showFilter)} className={`${showFilter ? "active" : ""}`}>
                                            <img src={iconFilter} alt="Filtrar" />
                                                Filtros 
                                            <i className="icon-chevron-down"></i>
                                        </button>
                                    </div>
                                </div>
                                <div className={`library-filters__categories ${showFilter ? "open" : ""}`}>
                                    <div className="library-filters__categories__item">
                                        <p>Fecha de publicación</p>
                                        <Select
                                            onChange={filteredByDate}
                                            options={Dates}
                                            className="select date-select"
                                            placeholder="Selecciona"
                                            value={selectedDate}
                                            closeMenuOnSelect={false}
                                            styles={styleSelect}
                                        />
                                        {showDateRange && (
                                            <DateRange
                                                editableDateInputs={true}
                                                onChange={(item => setDate([item.selection]))}
                                                moveRangeOnFirstSelection={false}
                                                ranges={date}
                                                showSelectionPreview={true}
                                                color="#FBD4E4"
                                                rangeColors={["#FBD4E4", "#FFFFFF"]}
                                                showDateDisplay={false}
                                                className={`date-range`}
                                                ref={dateRangeRef}
                                            />
                                        )}
                                    </div>
                                    <div className="library-filters__categories__item">
                                        <p>Categoría</p>
                                        <Select
                                            onChange={(option: any) => setSelectedCategory(option)}
                                            options={Categories}
                                            className="select category-select"
                                            placeholder="Selecciona"
                                            value={selectedCategory}
                                            isMulti
                                            components={{Option}}
                                            closeMenuOnSelect={false}
                                            hideSelectedOptions={false}
                                            styles={styleSelect}
                                        />
                                    </div>
                                </div>
                                <FilterMobile setActivedTheme={setActivedTheme} onChangeDate={filteredByDate} setSelectedCategory={setSelectedCategory}/>
                            </div>
                        </div>
                        <div className="o-library-section__container__results">
                            <p>Mostrando {filteredList.length} resultados para {`"${searchWordContent}"`}</p>
                        </div>
                        <div className="o-library-section__container__body">
                            <div className="library-layout">
                                {currentFilteredList && currentFilteredList.map((item, i) => (
                                    <MediaCard
                                        tituloMultimedia={item.tituloMultimedia}
                                        descripcion={item.descripcion}
                                        imagenPrincipal={item.imagenPrincipal}
                                        tituloBoton={item.tituloBoton}
                                        tematica={item.tematica}
                                        categoriaMultimedia={item.categoriaMultimedia}
                                        nuevoContenido={item.nuevoContenido}
                                        textoIntroductorio={item.textoIntroductorio}
                                        slug={item.slug}
                                        parentContentfulId={item.parentContentfulId}
                                        createdAt={item.createdAt}
                                    />
                                ))}
                                {currentFilteredList && currentFilteredList.length === 0 && (
                                    <div className="library-layout__nofound">
                                        <span>¡Vaya!</span>
                                        <p>
                                            No se encontró contenido con los filtros de búsqueda seleccionados.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="o-library-section__container__footer">
                            <Pagination
                                className="pagination-bar"
                                currentPage={currentPage}
                                totalCount={filteredList.length}
                                pageSize={PageSize}
                                onPageChange={(page) => {
                                    setCurrentPage(page);
                                    window.scrollTo({
                                        top: posRef.current ? posRef.current?.offsetTop - 90 : 0,
                                        behavior: "smooth",
                                    });
                                }}
                            />
                        </div>
                    </div>
                </div>
                </div>
            )}
        </> 
    )
}

export default LibrarySection
